import { defineStore } from 'pinia'

import {
  ActionType,
  ContentType,
  type LessonCompletionMigration,
  SyncEvent,
  UserInfo
} from '@/lib/sync'
import { SyncService } from '@/lib/sync/service'
import store from '@/store'

type ConfigureOptions = {
  syncIntervalSec?: number
}

const getAccountUserInfo = (): UserInfo | null => {
  const learningLanguage = store.getters['account/getActiveProfile'] as
    | string
    | undefined
  const nativeLanguage = store.getters['account/getCurrentLanguage'] as
    | string
    | undefined
  const udid = store.getters['account/getUserId'] as string | undefined

  if (!learningLanguage || !nativeLanguage || !udid) {
    return null
  }

  return {
    udid,
    learningLanguage,
    nativeLanguage
  }
}

export const useSyncStore = defineStore('sync', {
  state: () => ({
    _service: null as SyncService | null,
    syncIntervalSec: 86400 as number,
    appId: 'com.ewa.ewaweb' as string,
    lastSync: 0 as number,
    completed: {
      lesson: {} as Record<string, boolean>
    }
  }),

  actions: {
    configure(options: ConfigureOptions = {}) {
      const service = this.ensureService()

      if (typeof options.syncIntervalSec === 'number') {
        this.syncIntervalSec = options.syncIntervalSec
        service.setSyncInterval(options.syncIntervalSec)
      }
    },

    ensureService() {
      if (!this._service) {
        this._service = new SyncService({
          syncIntervalSec: this.syncIntervalSec,
          appId: this.appId,
          getUserInfo: getAccountUserInfo,
          applyEvents: (events) => {
            this.applyEvents(events)
          },
          onLastSyncChange: (timestamp) => {
            this.lastSync = timestamp
          }
        })
      }

      return this._service
    },

    setSyncIntervalSec(value: number) {
      this.syncIntervalSec = value
      this._service?.setSyncInterval(value)
    },

    async startSyncing() {
      const service = this.ensureService()
      service.startSyncing()
    },

    stopSyncing() {
      this._service?.stopSyncing()
    },

    async createEvent(
      contentType: ContentType,
      actionType: ActionType,
      modifier: (event: SyncEvent) => void
    ) {
      const service = this.ensureService()
      await service.createEvent(contentType, actionType, modifier)
    },

    async migrateCompletedLessons(lessons: LessonCompletionMigration[]) {
      if (!lessons?.length) {
        return
      }

      const service = this.ensureService()
      await service.saveLessonCompletionEvents(lessons)
    },

    async initCompletion() {
      const service = this.ensureService()
      await service.initCompletion()
    },

    applyEvents(events: SyncEvent[]) {
      for (const evt of events) {
        if (
          evt.content_type === ContentType.LESSON &&
          evt.action_type === ActionType.LESSON_COMPLETED &&
          evt.content_id
        ) {
          this.completed.lesson[evt.content_id] = true
        }
      }
    },

    isLessonCompleted(id: string): boolean | undefined {
      return this.completed.lesson[id]
    },

    stopListening() {
      this._service?.stopListeningFromServer()
    },

    shutdown() {
      this._service?.shutdown()
    },

    async getUniqueLessonEventsByActionType(): Promise<
      Map<ActionType, SyncEvent[]>
    > {
      const service = this.ensureService()
      const events = (await service.getStorageEvents()) ?? []

      const latestEventsByActionType = new Map<
        ActionType,
        Map<string, SyncEvent>
      >()

      latestEventsByActionType.set(
        ActionType.LESSON_STARTED,
        new Map<string, SyncEvent>()
      )
      latestEventsByActionType.set(
        ActionType.LESSON_COMPLETED,
        new Map<string, SyncEvent>()
      )

      for (const event of events) {
        if (event.content_type !== ContentType.LESSON || !event.content_id) {
          continue
        }

        const currentActionType = latestEventsByActionType.get(
          event.action_type
        )

        if (!currentActionType) {
          continue
        }

        const current = currentActionType.get(event.content_id)

        if (!current || event.created_at >= current.created_at) {
          currentActionType.set(event.content_id, event)
        }
      }

      return new Map([
        [
          ActionType.LESSON_STARTED,
          Array.from(
            latestEventsByActionType.get(ActionType.LESSON_STARTED).values()
          ).sort((a, b) => b.created_at - a.created_at)
        ],

        [
          ActionType.LESSON_COMPLETED,
          Array.from(
            latestEventsByActionType.get(ActionType.LESSON_COMPLETED).values()
          ).sort((a, b) => b.created_at - a.created_at)
        ]
      ])
    },

    async getStorageComparisonData(getNextLessonIdFn?: (lessonId: string) => string | null) {
      const lessonEventsByActionType =
        await this.getUniqueLessonEventsByActionType()

      const lessonUniqueCompletedEvents = lessonEventsByActionType.get(
        ActionType.LESSON_COMPLETED
      )
      const lessonUniqueStartedEvents = lessonEventsByActionType.get(
        ActionType.LESSON_STARTED
      )

      const lessonCompletedCount = lessonUniqueCompletedEvents.filter(
        (event) => event.value_integer > 0
      ).length

      const lessonCompletedStars = lessonUniqueCompletedEvents.reduce(
        (acc, event) => acc + (event.value_integer ?? 0),
        0
      )

      let actualLessonId = ''
      const lastCompletedEvent = lessonUniqueCompletedEvents?.[0]
      const lastStartedEvent = lessonUniqueStartedEvents?.[0]

      if (!lastCompletedEvent && !lastStartedEvent) {
        actualLessonId = ''
      } else if (
        lastStartedEvent?.created_at > lastCompletedEvent?.created_at
      ) {
        actualLessonId = lastStartedEvent.content_id
      } else if (lastCompletedEvent) {
        const completedLessonId = lastCompletedEvent.content_id
        actualLessonId = getNextLessonIdFn
          ? (getNextLessonIdFn(completedLessonId) ?? '')
          : completedLessonId
      }

      return {
        lessonCompletedCount,
        lessonCompletedStars,
        actualLessonId
      }
    }
  }
})
